import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Send, Wifi, WifiOff } from 'lucide-react';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChatParticipant, useChat } from '../hooks/useChat';
import { DEFAULT_AVATAR_URL, getInstrumentAvatarUrl } from '../shared/avatars';

interface MessagesLocationState {
  recipientId?: string;
  recipientName?: string;
  recipientInstrument?: string;
}

const MessagesPage: React.FC = () => {
  const { user } = useAuth();
  const {
    conversations,
    activeConversation,
    activeChatId,
    status,
    error,
    selectConversation,
    clearConversationSelection,
    sendMessage,
  } = useChat(user?.id);
  const location = useLocation();
  const [pendingRecipient, setPendingRecipient] = useState<ChatParticipant | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredConversations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter((conversation) => {
      const name = conversation.participant?.name ?? conversation.participantId;
      return name.toLowerCase().includes(term);
    });
  }, [conversations, searchTerm]);

  const formatMessageTime = (timestamp: number) => format(new Date(timestamp), 'HH:mm');
  const formatConversationDate = (timestamp: number) => format(new Date(timestamp), 'MMM d, yyyy');

  const statusMeta = useMemo(() => {
    switch (status) {
      case 'connected':
        return {
          label: 'Connected',
          className: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
          icon: <Wifi className="w-3.5 h-3.5" />,
        };
      case 'connecting':
        return {
          label: 'Connecting',
          className: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
          icon: <Wifi className="w-3.5 h-3.5" />,
        };
      case 'error':
        return {
          label: 'Offline',
          className: 'bg-rose-500/15 text-rose-200 border-rose-500/30',
          icon: <WifiOff className="w-3.5 h-3.5" />,
        };
      case 'disconnected':
        return {
          label: 'Disconnected',
          className: 'bg-slate-500/15 text-slate-200 border-slate-500/30',
          icon: <WifiOff className="w-3.5 h-3.5" />,
        };
      default:
        return {
          label: 'Idle',
          className: 'bg-slate-500/10 text-slate-200 border-slate-500/20',
          icon: <WifiOff className="w-3.5 h-3.5" />,
        };
    }
  }, [status]);

  useEffect(() => {
    const state = location.state as MessagesLocationState | null;
    if (!state?.recipientId) return;
    if (user && state.recipientId === user.id) return;
    const recipientName = state.recipientName?.trim();
    setPendingRecipient({
      id: state.recipientId,
      name: recipientName && recipientName.length > 0 ? recipientName : state.recipientId,
      instrument: state.recipientInstrument || undefined,
    });
  }, [location.state, user]);

  useEffect(() => {
    if (!pendingRecipient) return;
    const existing = conversations.find(
      (conversation) => conversation.participantId === pendingRecipient.id
    );
    if (existing) {
      selectConversation(existing.id);
      return;
    }

    clearConversationSelection();
  }, [clearConversationSelection, conversations, pendingRecipient, selectConversation]);

  useEffect(() => {
    if (!pendingRecipient || !activeConversation) return;
    if (activeConversation.participantId === pendingRecipient.id) {
      setPendingRecipient(null);
    }
  }, [activeConversation, pendingRecipient]);

  const isComposingNewConversation =
    !!pendingRecipient && activeConversation?.participantId !== pendingRecipient.id;

  const displayedConversation = isComposingNewConversation ? null : activeConversation;

  const handleSelectConversation = (chatId: string) => {
    setPendingRecipient(null);
    selectConversation(chatId);
  };

  const handleSendMessage = () => {
    if (!user) return;
    const trimmed = newMessage.trim();
    if (!trimmed) return;

    const recipientId = isComposingNewConversation
      ? pendingRecipient?.id
      : activeConversation?.participantId ?? pendingRecipient?.id;
    if (!recipientId) return;

    const success = sendMessage({
      chatId: isComposingNewConversation ? undefined : activeConversation?.id,
      recipientId,
      content: trimmed,
    });
    if (success) {
      setNewMessage('');
    }
  };

  const canSend = !!user && status === 'connected' && (!!displayedConversation || !!pendingRecipient);
  const hasActiveView = !!displayedConversation || !!pendingRecipient;
  const activeRecipientName =
    pendingRecipient?.name ??
    displayedConversation?.participant?.name ??
    displayedConversation?.participantId ??
    pendingRecipient?.name ??
    'Unknown musician';
  const activeRecipientInstrument =
    pendingRecipient?.instrument ?? displayedConversation?.participant?.instrument ?? 'Musician';
  const activeRecipientAvatar = getInstrumentAvatarUrl(
    pendingRecipient?.instrument ?? displayedConversation?.participant?.instrument
  );

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const frameId = window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [displayedConversation?.id, displayedConversation?.messages.length]);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold text-white">Messages</h1>
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${statusMeta.className}`}
        >
          {statusMeta.icon}
          <span>{statusMeta.label}</span>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden grid grid-cols-1 md:grid-cols-3 min-h-[70vh]">
        <div className="border-r border-gray-700">
          <div className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search conversations"
                className="w-full pl-10 pr-4 py-2 bg-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {filteredConversations.length === 0 ? (
              <div className="text-sm text-gray-400 px-2 py-6 text-center">
                No conversations yet. Start a chat to see messages here.
              </div>
            ) : (
              <div className="space-y-1">
                {filteredConversations.map((conversation) => {
                  const lastMessage = conversation.lastMessage;
                  const displayName = conversation.participant?.name ?? conversation.participantId;
                  const instrument = conversation.participant?.instrument ?? 'Musician';
                  const avatarUrl = getInstrumentAvatarUrl(conversation.participant?.instrument);

                  return (
                    <button
                      key={conversation.id}
                      className={`w-full text-left p-3 rounded-md transition-colors ${
                        activeChatId === conversation.id
                          ? 'bg-purple-900 bg-opacity-50'
                          : 'hover:bg-gray-700'
                      }`}
                      onClick={() => handleSelectConversation(conversation.id)}
                    >
                      <div className="flex items-center">
                        <div className="relative">
                          <img
                            src={avatarUrl}
                            alt={displayName}
                            className="w-10 h-10 rounded-full object-cover"
                            onError={(event) => {
                              event.currentTarget.src = DEFAULT_AVATAR_URL;
                              event.currentTarget.onerror = null;
                            }}
                          />
                          {conversation.unreadCount > 0 && (
                            <span className="absolute -bottom-1 -right-1 min-w-[1.25rem] h-5 px-1 text-[10px] font-semibold rounded-full bg-purple-500 text-white flex items-center justify-center">
                              {conversation.unreadCount}
                            </span>
                          )}
                        </div>

                        <div className="ml-3 flex-grow overflow-hidden">
                          <div className="flex justify-between items-center">
                            <h3 className="text-white font-medium truncate">{displayName}</h3>
                            {lastMessage && (
                              <span className="text-xs text-gray-400">
                                {formatConversationDate(lastMessage.createdAt)}
                              </span>
                            )}
                          </div>

                          <p className="text-xs text-gray-500 mt-1">{instrument}</p>
                          <p className="text-sm text-gray-400 truncate">
                            {lastMessage ? lastMessage.content : 'No messages yet'}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-2 flex flex-col max-h-[70vh]">
          {hasActiveView ? (
            <>
              <div className="p-4 border-b border-gray-700 flex items-center justify-between gap-4">
                <div className="flex items-center">
                  <img
                    src={activeRecipientAvatar}
                    alt={activeRecipientName}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(event) => {
                      event.currentTarget.src = DEFAULT_AVATAR_URL;
                      event.currentTarget.onerror = null;
                    }}
                  />

                  <div className="ml-3">
                    <h3 className="text-white font-medium">{activeRecipientName}</h3>
                    <p className="text-xs text-gray-400">{activeRecipientInstrument}</p>
                  </div>
                </div>
                {error && <span className="text-xs text-rose-300">{error}</span>}
              </div>

              <div
                ref={messagesContainerRef}
                className="flex-grow p-4 overflow-y-auto space-y-4 themed-scrollbar"
              >
                {!displayedConversation ? (
                  <div className="text-center text-gray-400 text-sm py-10">
                    Start a conversation by sending a message.
                  </div>
                ) : displayedConversation.messages.length === 0 ? (
                  <div className="text-center text-gray-400 text-sm py-10">
                    No messages yet. Say hello to start the conversation.
                  </div>
                ) : (
                  displayedConversation.messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.senderId === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`max-w-[80%] rounded-lg px-4 py-2 ${
                          message.senderId === user?.id
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-700 text-white'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{message.content}</p>
                        <p
                          className={`text-xs mt-1 ${
                            message.senderId === user?.id ? 'text-purple-300' : 'text-gray-400'
                          }`}
                        >
                          {formatMessageTime(message.createdAt)}
                        </p>
                      </motion.div>
                    </div>
                  ))
                )}
              </div>

              <div className="p-4 border-t border-gray-700">
                <div className="flex">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={
                      canSend ? 'Type a message...' : 'Connect to chat service to send messages...'
                    }
                    className="flex-grow px-4 py-2 bg-gray-700 rounded-l-md text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-60"
                    onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                    disabled={!canSend}
                  />
                  <button
                    onClick={handleSendMessage}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 rounded-r-md transition-colors disabled:opacity-60"
                    disabled={!canSend}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-grow flex items-center justify-center">
              <p className="text-gray-400">Select a conversation to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
